import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../Api/baseQueryWithReauth';
import { TagTypes } from '../../../Api/Types/tagTypes';
import {
  GetAutoOutgoingCandidatesResult,
  KsefOutgoingCandidatesQuery,
  KsefOutgoingEnumsDto,
  KsefOutgoingEligibilityInputDto,
  KsefOutgoingSemanticEventsQuery,
  KsefOutgoingSemanticEventsResult,
  KsefOutgoingSemanticMetricsQuery,
  KsefOutgoingSemanticMetricsResult,
  KsefOutgoingRecordsQuery,
  KsefOutgoingRecordsResult,
  BackfillKsefInvoiceNumbersResult,
  KsefOutgoingStatusDto,
  OutgoingUpoDownloadUrlResult,
  OutgoingSnapshotPreviewResult,
  PollOutgoingInvoiceStatusResponse,
  PrepareOutgoingInvoiceResponse,
  SubmitOutgoingInvoiceResponse,
} from '../Types/ksefOutgoing.types';

interface InvoiceActionArgs {
  invoiceId: number;
  eligibilityInput?: KsefOutgoingEligibilityInputDto;
}

export const ksefOutgoingApi = createApi({
  reducerPath: 'ksefOutgoingApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [TagTypes.KsefOutgoing, TagTypes.KsefOutgoingEnums, TagTypes.KsefOutgoingStatus],
  endpoints: (builder) => ({
    getKsefOutgoingStatus: builder.query<KsefOutgoingStatusDto, void>({
      query: () => ({ url: '/ksef/outgoing/status' }),
      providesTags: [TagTypes.KsefOutgoingStatus],
    }),

    getKsefOutgoingEnums: builder.query<KsefOutgoingEnumsDto, void>({
      query: () => ({
        url: '/ksef/outgoing/enums',
      }),
      providesTags: [TagTypes.KsefOutgoingEnums],
    }),

    getAutoOutgoingCandidates: builder.query<
      GetAutoOutgoingCandidatesResult,
      KsefOutgoingCandidatesQuery
    >({
      query: ({ take = 200, issueDateFrom }) => ({
        url: '/ksef/outgoing/auto-candidates',
        params: {
          take,
          ...(issueDateFrom ? { issueDateFrom } : {}),
        },
      }),
      providesTags: [TagTypes.KsefOutgoing],
    }),

    getOutgoingRecords: builder.query<KsefOutgoingRecordsResult, KsefOutgoingRecordsQuery | void>({
      query: (params) => {
        const queryParams: Record<string, string | number> = {};

        if (params?.take !== undefined) {
          queryParams.take = params.take;
        }

        if (params?.pageNumber !== undefined) {
          queryParams.pageNumber = params.pageNumber;
        }

        if (params?.pageSize !== undefined) {
          queryParams.pageSize = params.pageSize;
        }

        if (params?.createdDateFrom) {
          queryParams.createdDateFrom = params.createdDateFrom;
        }

        if (params?.createdDateTo) {
          queryParams.createdDateTo = params.createdDateTo;
        }

        return {
          url: '/ksef/outgoing/records',
          params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
        };
      },
      providesTags: [TagTypes.KsefOutgoing],
    }),

    prepareOutgoingInvoice: builder.mutation<PrepareOutgoingInvoiceResponse, InvoiceActionArgs>({
      query: ({ invoiceId, eligibilityInput }) => ({
        url: `/ksef/outgoing/${invoiceId}/prepare`,
        method: 'POST',
        ...(eligibilityInput ? { body: eligibilityInput } : {}),
      }),
    }),

    submitOutgoingInvoice: builder.mutation<SubmitOutgoingInvoiceResponse, InvoiceActionArgs>({
      query: ({ invoiceId, eligibilityInput }) => ({
        url: `/ksef/outgoing/${invoiceId}/submit`,
        method: 'POST',
        ...(eligibilityInput ? { body: eligibilityInput } : {}),
      }),
    }),

    pollOutgoingInvoiceStatus: builder.mutation<PollOutgoingInvoiceStatusResponse, number>({
      query: (invoiceId) => ({
        url: `/ksef/outgoing/${invoiceId}/poll-status`,
        method: 'POST',
      }),
    }),

    backfillKsefInvoiceNumbers: builder.mutation<BackfillKsefInvoiceNumbersResult, void>({
      query: () => ({
        url: '/ksef/outgoing/admin/backfill-ksef-numbers',
        method: 'POST',
      }),
      invalidatesTags: [TagTypes.KsefOutgoing],
    }),

    getOutgoingUpoDownloadUrl: builder.query<OutgoingUpoDownloadUrlResult, number>({
      query: (invoiceId) => ({
        url: `/ksef/outgoing/${invoiceId}/upo/download-url`,
      }),
      providesTags: [TagTypes.KsefOutgoing],
    }),

    getOutgoingSnapshotPreview: builder.query<OutgoingSnapshotPreviewResult, number>({
      query: (invoiceId) => ({
        url: `/ksef/outgoing/${invoiceId}/snapshot-preview`,
      }),
      providesTags: [TagTypes.KsefOutgoing],
    }),

    getSemanticMetrics: builder.query<
      KsefOutgoingSemanticMetricsResult,
      KsefOutgoingSemanticMetricsQuery
    >({
      query: ({ fromUtc, toUtc }) => ({
        url: '/ksef/outgoing/semantic/metrics',
        params: {
          ...(fromUtc ? { fromUtc } : {}),
          ...(toUtc ? { toUtc } : {}),
        },
      }),
      providesTags: [TagTypes.KsefOutgoing],
    }),

    getSemanticEvents: builder.query<KsefOutgoingSemanticEventsResult, KsefOutgoingSemanticEventsQuery>({
      query: ({ fromUtc, toUtc, take = 100 }) => ({
        url: '/ksef/outgoing/semantic/events',
        params: {
          ...(fromUtc ? { fromUtc } : {}),
          ...(toUtc ? { toUtc } : {}),
          take,
        },
      }),
      providesTags: [TagTypes.KsefOutgoing],
    }),
  }),
});

export const {
  useGetKsefOutgoingStatusQuery,
  useGetKsefOutgoingEnumsQuery,
  useGetAutoOutgoingCandidatesQuery,
  useGetOutgoingRecordsQuery,
  useLazyGetOutgoingRecordsQuery,
  usePrepareOutgoingInvoiceMutation,
  useSubmitOutgoingInvoiceMutation,
  usePollOutgoingInvoiceStatusMutation,
  useBackfillKsefInvoiceNumbersMutation,
  useLazyGetOutgoingUpoDownloadUrlQuery,
  useLazyGetOutgoingSnapshotPreviewQuery,
  useGetSemanticMetricsQuery,
  useGetSemanticEventsQuery,
} = ksefOutgoingApi;
